import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, Clock, CheckCircle, XCircle, CreditCard, TrendingUp, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBilling() {
  const [data, setData] = useState({ subscriptions: [], total: 0, stats: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [processing, setProcessing] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/billing', { params: { status: statusFilter } });
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [statusFilter]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await api.put(`/admin/billing/${id}/approve`);
      toast.success('Subscription disetujui');
      fetch();
    } catch (e) {
      toast.error('Gagal menyetujui subscription');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Tolak subscription ini?')) return;
    setProcessing(id);
    try {
      await api.put(`/admin/billing/${id}/reject`);
      toast.success('Subscription ditolak');
      fetch();
    } catch (e) {
      toast.error('Gagal menolak subscription');
    } finally {
      setProcessing(null);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const statusColor = (s) => {
    if (s === 'active' || s === 'paid') return 'bg-emerald-100 text-emerald-700';
    if (s === 'pending') return 'bg-amber-100 text-amber-700';
    if (s === 'rejected' || s === 'canceled') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const stats = data.stats || {};

  return (
    <div data-testid="admin-billing-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>SaaS Billing</h1>
          <p className="text-sm text-slate-500">Kelola subscription dan pembayaran tenant</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <DollarSign size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-bold">Rp {fmt(stats.total_revenue)}</p>
              <p className="text-xs text-slate-500">Total Revenue</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold">Rp {fmt(stats.pending_amount)}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users size={20} className="text-[#2D6CDF]" />
            </div>
            <div>
              <p className="text-xl font-bold">{stats.active_count || 0}</p>
              <p className="text-xs text-slate-500">Active Subs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
              <CreditCard size={20} className="text-violet-600" />
            </div>
            <div>
              <p className="text-xl font-bold">{stats.pending_count || 0}</p>
              <p className="text-xs text-slate-500">Pending Payment</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Daftar Subscription</CardTitle>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="billing-status-filter">
              <SelectValue placeholder="Semua Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Tenant</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Paket</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Periode</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Jumlah</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
                  <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                </tr></thead>
                <tbody>
                  {data.subscriptions.map((s) => (
                    <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{s.tenant?.name || '-'}</p>
                        <p className="text-xs text-slate-500">{s.tenant?.subdomain}</p>
                      </td>
                      <td className="py-3 px-4 font-medium">{s.package?.name || s.package_name}</td>
                      <td className="py-3 px-4 text-slate-600 capitalize">{s.billing_cycle}</td>
                      <td className="py-3 px-4 font-medium">Rp {fmt(s.amount)}</td>
                      <td className="py-3 px-4"><Badge className={statusColor(s.status)}>{s.status}</Badge></td>
                      <td className="py-3 px-4 text-slate-500">{new Date(s.created_at).toLocaleDateString('id-ID')}</td>
                      <td className="py-3 px-4 text-right">
                        {s.status === 'pending' && (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={() => handleApprove(s.id)} disabled={processing === s.id} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`approve-${s.id}`}>
                              {processing === s.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleReject(s.id)} disabled={processing === s.id} className="text-red-500 border-red-200 hover:bg-red-50" data-testid={`reject-${s.id}`}>
                              <XCircle size={14} className="mr-1" /> Tolak
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!data.subscriptions.length && <tr><td colSpan={7} className="py-12 text-center text-slate-400">Belum ada subscription</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
